import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useNativeChrome } from '../hooks/useNativeChrome';
import { useAuth } from '../context/AuthContext';
import { BrandMark } from '../components/ui/bits';
import {
  AuthHeader,
  AuthPage,
  AuthTitle,
  ErrorNote,
  OrDivider,
  PasswordField,
  PrimaryButton,
  SocialButtons,
  StrengthMeter,
  TextField,
  authMessage,
  passwordStrength
} from '../components/auth/parts';
import { Mail } from '../components/ui/icons';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Step 1 of sign-up: create the auth account (or use a social provider). The public
// profile (name, @username, avatar) is collected next, on the profile step, once the
// player is authenticated — so the username availability check can read Firestore.
export default function CreateAccount() {
  useNativeChrome();
  const navigate = useNavigate();
  const { register, signInWithGoogle, signInWithApple } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Social sign-in ships in the native apps only; the web uses email for now.
  const isNative = Capacitor.isNativePlatform();

  const valid = EMAIL_RE.test(email.trim()) && passwordStrength(password) >= 1;

  async function run(fn: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      // On success the app gate routes the new account to the profile step.
    } catch (err) {
      setError(authMessage(err));
      setBusy(false);
    }
  }

  function onContinue(e: FormEvent) {
    e.preventDefault();
    if (!valid) {
      setError(
        !EMAIL_RE.test(email.trim())
          ? 'That email doesn’t look right.'
          : 'Password must be at least 6 characters.'
      );
      return;
    }
    run(() => register(email.trim(), password));
  }

  return (
    <AuthPage>
      <AuthHeader backLabel="Welcome" onBack={() => navigate('/welcome')} />

      <div className="flex flex-1 flex-col">
        <BrandMark size={44} className="mt-2" />
        <div className="mt-4">
          <AuthTitle title="Create your account" subtitle="Call the games. Climb the divisions." />
        </div>

        {isNative && (
          <>
            <div className="mt-6">
              <SocialButtons
                disabled={busy}
                onApple={() => run(signInWithApple)}
                onGoogle={() => run(signInWithGoogle)}
              />
            </div>

            <div className="my-5">
              <OrDivider label="or sign up with email" />
            </div>
          </>
        )}

        <form onSubmit={onContinue} className={`flex flex-col gap-4 ${isNative ? '' : 'mt-6'}`}>
          <TextField
            label="Email"
            type="email"
            inputMode="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            autoComplete="email"
            prefix={<Mail width={18} height={18} />}
          />
          <div>
            <PasswordField
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
            <StrengthMeter value={password} />
          </div>

          {error && <ErrorNote>{error}</ErrorNote>}

          <div className="mt-1">
            <PrimaryButton type="submit" busy={busy} disabled={busy}>
              Create account
            </PrimaryButton>
          </div>
        </form>

        <p className="mt-4 text-center text-[12px] font-medium leading-[1.5] text-ink3">
          By continuing you agree to our{' '}
          <a
            href="https://headtohead.dk/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-ink2 underline"
          >
            Terms
          </a>{' '}
          &{' '}
          <a
            href="https://headtohead.dk/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-ink2 underline"
          >
            Privacy Policy
          </a>
          .
        </p>

        <div className="flex-1" />

        <p className="pb-2 pt-5 text-center text-[14px] font-medium text-ink2">
          Already have an account?
        </p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="tap mb-2 flex w-full items-center justify-center rounded-[14px] border-[1.5px] border-accent-ink/30 bg-white py-[14px] text-[15px] font-extrabold tracking-snug text-accent-ink"
        >
          Log in
        </button>
      </div>
    </AuthPage>
  );
}
